import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Skeleton from '@mui/lab/Skeleton';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import JWVideoPlayer from 'components/JWVideoPlayer';
import updateUserEpisodeDetailsMutation from '../../graphql/mutations/updateUserEpisodeDetails.graphql';
import { useVideoPlayerContext } from '../../context/VideoPlayerContext';
import { useSiteSettings } from 'context/SiteSettingsContext';
import livestreamShape from 'shapes/livestreamShape';
import { useAuth } from 'context/AuthContext';

const SkeletonContainer = styled.div`
  height: 0;
  overflow: hidden;
  padding-top: 56.25%;
  position: relative;
`;

const PlayerSkeleton = styled(Skeleton).attrs({ variant: 'rect' })`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

VideoPlayer.propTypes = {
  autoplay: PropTypes.oneOf([false, true, 'muted', 'play', 'any']),
  currentTime: PropTypes.number,
  episodeId: PropTypes.string,
  file: PropTypes.string,
  live: PropTypes.bool,
  mountId: PropTypes.string,
  onEnded: PropTypes.func,
  onSuccess: PropTypes.func,
  pictureInPicture: PropTypes.bool,
  playbackPosition: PropTypes.number,
  playerSettings: livestreamShape,
  poster: PropTypes.string,
  videoId: PropTypes.string,
};

function VideoPlayer({
  currentTime,
  episodeId,
  file,
  videoId,
  poster,
  autoplay = false,
  onSuccess,
  live = false,
  mountId,
  playerSettings = null,
  playbackPosition,
  onEnded,
  ...props
}) {
  const { siteSettings } = useSiteSettings();
  const { setVideoDuration } = useVideoPlayerContext();
  const [isMounted, setIsMounted] = useState(false);
  const playerRef = useRef(null);
  const history = useHistory();
  const [updateUserEpisodeDetails] = useMutation(updateUserEpisodeDetailsMutation);
  const { user } = useAuth();

  useEffect(() => {
    let interval;

    if (history.location.pathname !== '/' && episodeId && user) { // do not store user episode position on home page - live player
      interval = setInterval(() => {
        handleUpdateUserEpisodeDetails();
      }, 10000) // 10 seconds
    }

    return () => clearInterval(interval);
  }, [episodeId, user])

  async function handleUpdateUserEpisodeDetails() {
    if (playerRef.current) {
      const playbackPosition = playerRef.current.getPosition();
      const episodeDuration = playerRef.current.getDuration();

      // Confirm we have the episodeDuration available before trying to save (race condition).
      if (playbackPosition > 0 && episodeDuration) {
        await updateUserEpisodeDetails({
          variables: {
            input: { videoId: episodeId, playbackPosition, episodeDuration }
          }
        });
      }
    }
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsMounted(true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const settingsKey = live ? 'livePlayerSettings' : 'vodPlayerSettings';

  if (!isMounted) {
    return null;
  }

  if (!playerSettings && (!siteSettings || !siteSettings[settingsKey])) {
    return (
      <SkeletonContainer>
        <PlayerSkeleton />
      </SkeletonContainer>
    );
  }

  const settings = playerSettings || siteSettings[settingsKey];

  if (!settings) {
    throw new Error('No player settings available in VideoPlayer component');
  }

  const libraryUrl = settings?.jwLibraryUrl;
  const episodeVideoId = videoId;

  const handleDuration = (duration) => {
    if (Number.isFinite(duration) && duration > 0) {
      setVideoDuration(duration);
    }
  };

  const handlePlayerReady = (success) => {
    playerRef.current = success.ref;

    if (onSuccess) {
      onSuccess(success);
    }
  };

  return (
    <JWVideoPlayer
      key={`jw-${episodeId}-${live ? 'live' : 'vod'}`}
      autoplay={autoplay}
      file={file}
      libraryUrl={libraryUrl}
      live={live}
      onComplete={onEnded}
      onDuration={handleDuration}
      onSuccess={handlePlayerReady}
      poster={poster}
      startTime={currentTime || playbackPosition} // Pass as config instead of seeking
      videoId={episodeVideoId}
      {...props}
    />
  );
}

export default React.memo(VideoPlayer);
